import { ApolloClient, HttpLink, InMemoryCache, Observable, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';

import {
  getStoredRefresh,
  setStoredRefresh,
  useCustomerAuthStore,
  type CustomerProfile,
} from './customerAuthStore';

// Same-origin by default (see apps/web/src/shared/api/apolloClient.ts).
const API_URL = (import.meta.env.VITE_API_URL as string | undefined) || '/graphql';

const REFRESH_MUTATION = `
  mutation CustomerRefresh($input: CustomerRefreshInput!) {
    customerRefresh(input: $input) {
      access_token
      refresh_token
      customer { id first_name last_name full_name phone email }
    }
  }
`;

interface RefreshResult {
  access_token: string;
  refresh_token: string;
  customer: CustomerProfile;
}

let in_flight: Promise<string | null> | null = null;

/** Exchange the stored refresh token for a fresh access token. Updates the auth store. */
async function customerRefresh(): Promise<string | null> {
  if (in_flight) return in_flight;
  in_flight = (async () => {
    const refresh_token = getStoredRefresh();
    if (!refresh_token) return null;
    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: REFRESH_MUTATION, variables: { input: { refresh_token } } }),
      });
      const json = (await res.json()) as { data?: { customerRefresh?: RefreshResult | null } };
      const payload = json.data?.customerRefresh;
      if (!payload) {
        useCustomerAuthStore.getState().clear();
        return null;
      }
      setStoredRefresh(payload.refresh_token);
      useCustomerAuthStore.getState().setSession(payload.access_token, payload.customer);
      return payload.access_token;
    } catch {
      return null;
    } finally {
      in_flight = null;
    }
  })();
  return in_flight;
}

const auth_link = setContext((_request, prev) => {
  const headers = (prev.headers as Record<string, string>) ?? {};
  const token = useCustomerAuthStore.getState().access_token;
  return { headers: { ...headers, ...(token ? { Authorization: `Bearer ${token}` } : {}) } };
});

const error_link = onError(({ graphQLErrors, operation, forward }) => {
  const unauthenticated = (graphQLErrors ?? []).some(
    (e) => e.extensions?.code === 'UNAUTHENTICATED' || /unauthorized|unauthenticated/i.test(e.message),
  );
  if (!unauthenticated) return;
  return new Observable((observer) => {
    void customerRefresh().then((token) => {
      if (!token) {
        observer.error(new Error('customer_session_expired'));
        return;
      }
      operation.setContext(({ headers = {} }: { headers?: Record<string, string> }) => ({
        headers: { ...headers, Authorization: `Bearer ${token}` },
      }));
      forward(operation).subscribe(observer);
    });
  });
});

export const portalApolloClient = new ApolloClient({
  link: from([error_link, auth_link, new HttpLink({ uri: API_URL })]),
  cache: new InMemoryCache(),
});

/** On portal load, try to restore a session from the stored refresh token. */
export async function bootstrapPortalSession(): Promise<boolean> {
  const token = await customerRefresh();
  return Boolean(token);
}
